import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ApexAnnotations,
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { distinctUntilChanged, finalize, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { TeachCompensationService } from 'src/app/services/api/teach-compensation/teach-compensation.service';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import {
  CompensationSummaryParams,
  CompensationSummaryResponse,
} from 'src/app/services/models/teach-compensation/teach-compensation.model';
import { SharedModule } from 'src/shared/shared.module';

interface CompensationSummaryMetric {
  label: string;
  value: string | number;
  icon?: string;
  tone?: 'default' | 'success' | 'warning';
  unit?: string;
}

interface CompensationSummaryInsight {
  label: string;
  value: string;
  detail: string;
  icon: string;
  tone: 'success' | 'danger' | 'document';
}

interface CompensationCalculationRule {
  label: string;
  value: string;
}

interface CompensationCalculationRow {
  label: string;
  undergraduate: string;
  graduate: string;
  total: string;
  emphasis?: boolean;
  warning?: boolean;
}

interface CompensationFormulaCard {
  title: string;
  formula: string;
  icon: string;
}

interface RequestStatusChartOptions {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  colors: string[];
  dataLabels: ApexDataLabels;
  legend: ApexLegend;
  plotOptions: ApexPlotOptions;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
}

interface WorkloadChartOptions {
  annotations: ApexAnnotations;
  series: ApexAxisChartSeries;
  chart: ApexChart;
  colors: string[];
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  grid: ApexGrid;
  legend: ApexLegend;
  plotOptions: ApexPlotOptions;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
}

@Component({
  selector: 'app-compensation-summary-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NgApexchartsModule,
    ReactiveFormsModule,
    SharedModule,
  ],
  templateUrl: './compensation-summary-dashboard.component.html',
  styleUrl: './compensation-summary-dashboard.component.scss',
})
export class CompensationSummaryDashboardComponent implements OnInit {
  form!: FormGroup;
  summary = signal<CompensationSummaryResponse>(this.createEmptySummary());
  isLoading = signal(false);
  hasLoadError = signal(false);
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];

  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private studyPeriodService = inject(StudyPeriodService);
  private teachCompensationService = inject(TeachCompensationService);
  private academicTerms: AcademicTermYearModel[] = [];

  get currentUserName(): string {
    const user = this.currentUser()?.user;
    const fullName = user?.full_name;

    return fullName != null && fullName.trim() !== '' ? fullName : 'ผู้ใช้งานปัจจุบัน';
  }

  get currentUserEmail(): string {
    const user = this.currentUser()?.user;
    const email = user?.email;

    return email != null && email.trim() !== '' ? email : '-';
  }

  get currentUserDepartment(): string {
    const user = this.currentUser()?.user;

    return user == null ? '-' : user.department.name;
  }

  get academicTermLabel(): string {
    const term = this.summary().academic_term;

    if (term == null) {
      return '-';
    }

    return `${term.name}/${this.toBuddhistYear(term.year)}`;
  }

  get academicYearLabel(): string {
    const year = this.summary().academic_term?.year;

    return year == null ? '-' : String(this.toBuddhistYear(year));
  }

  get requestMetrics(): CompensationSummaryMetric[] {
    const summary = this.summary();

    return [
      { label: 'คำขอทั้งหมด', value: summary.total_request_count, icon: 'bi-file-earmark-text', unit: 'รายการ' },
      { label: 'รับรองแล้ว', value: summary.approved_request_count, icon: 'bi-check-lg', tone: 'success', unit: 'รายการ' },
      { label: 'ยังไม่รับรอง', value: summary.pending_request_count, icon: 'bi-clock', tone: 'warning', unit: 'รายการ' },
      { label: 'ชั่วโมงที่ขอทั้งหมด', value: this.formatPlainNumber(summary.total_requested_hours), icon: 'bi-clock-history', unit: 'ชม.' },
    ];
  }

  get workloadMetrics(): CompensationSummaryMetric[] {
    const summary = this.summary();

    return [
      { label: 'ภาระงานขั้นต่ำ', value: this.formatHours(summary.minimum_teaching_workload) },
      { label: 'ภาระงานตามตารางสอน', value: this.formatHours(summary.teaching_workload) },
      { label: 'ภาระงานที่สอนชดเชย', value: this.formatHours(summary.makeup_teaching_workload) },
      { label: 'ภาระงานรวม', value: this.formatHours(summary.total_teaching_workload) },
      { label: 'ภาระงานเกิน', value: this.formatHours(summary.excess_hours), tone: 'success' },
    ];
  }

  get regularCurriculumRules(): CompensationCalculationRule[] {
    return [
      { label: 'อ./ผศ./รศ./ศ.', value: '150 ชม.' },
      { label: 'ผู้บริหาร (คณบดี, รองอธิการบดี, อธิการบดี)', value: '45 ชม.' },
      { label: 'รองคณบดี / ผู้อำนวยการกอง', value: '90 ชม.' },
    ];
  }

  get compensationRateRules(): CompensationCalculationRule[] {
    return [
      { label: 'ระดับปริญญาตรี', value: '400 บาท/ชม.' },
      { label: 'ระดับปริญญาโท-เอก', value: '540 บาท/ชม.' },
      { label: 'เพดานค่าตอบแทนต่อท่าน', value: '30,000 บาท/ท่าน' },
    ];
  }

  get compensationCalculationRows(): CompensationCalculationRow[] {
    return [
      { label: 'จำนวนชั่วโมงที่สอนจริง', undergraduate: '174', graduate: '43.5', total: '217.5' },
      { label: 'ภาระที่ต้องสอน', undergraduate: '75', graduate: '75', total: '150' },
      { label: 'ชั่วโมงเกินภาระงาน', undergraduate: '99', graduate: '-31.5', total: '67.5', warning: true },
      { label: 'ชั่วโมงเบิกได้', undergraduate: '99', graduate: '-31.5', total: '67.5', emphasis: true, warning: true },
      { label: 'อัตราค่าตอบแทน (บาท/ชม.)', undergraduate: '400', graduate: '540', total: '-' },
      { label: 'ค่าตอบแทนเกินภาระงาน (บาท)', undergraduate: '27,000.00', graduate: '-', total: '27,000.00' },
    ];
  }

  get internationalFormulaCards(): CompensationFormulaCard[] {
    return [
      { title: 'บรรยาย 1-14 คน', formula: 'ชั่วโมงสอน × 1,000 × นักศึกษา ÷ 15', icon: 'bi-people' },
      { title: 'บรรยาย 15 คนขึ้นไป', formula: 'ชั่วโมงสอน × 1,000', icon: 'bi-people-fill' },
      { title: 'ปฏิบัติ 1-9 คน', formula: '10,000 × นักศึกษา ÷ 10', icon: 'bi-flask' },
      { title: 'ปฏิบัติ 10 คนขึ้นไป', formula: '10,000', icon: 'bi-flask-fill' },
    ];
  }

  get requestApprovalRate(): string {
    return `${this.formatPercent(this.summary().approved_request_count, this.summary().total_request_count)}%`;
  }

  get lastUpdatedLabel(): string {
    const importedAt = this.summary().erp_comparison.imported_at;

    if (importedAt == null || importedAt.trim() === '') {
      return 'ข้อมูล ณ ปัจจุบัน';
    }

    return `ข้อมูล ณ วันที่ ${this.formatDateTime(importedAt)}`;
  }

  get insights(): CompensationSummaryInsight[] {
    const summary = this.summary();

    return [
      {
        label: 'ภาพรวมการรับรอง',
        value: `รับรองแล้ว ${summary.approved_request_count} รายการ (${this.requestApprovalRate})`,
        detail: `จากคำขอทั้งหมด ${summary.total_request_count} รายการ`,
        icon: 'bi-check-lg',
        tone: 'success',
      },
      {
        label: 'ภาระงานเกิน',
        value: `เกินเกณฑ์ ${this.formatHours(summary.excess_hours)}`,
        detail: `ภาระงานรวม ${this.formatHours(summary.total_teaching_workload)}`,
        icon: 'bi-graph-up-arrow',
        tone: 'danger',
      },
      {
        label: 'พร้อมส่งต่อการเบิก',
        value: `${summary.approved_request_count} รายการ (${this.formatHours(summary.approved_hours)})`,
        detail: 'อ้างอิงรายการที่รับรองแล้ว',
        icon: 'bi-file-earmark-check',
        tone: 'document',
      },
    ];
  }

  get requestStatusChartOptions(): RequestStatusChartOptions {
    const summary = this.summary();

    return {
      series: [
        summary.approved_request_count,
        summary.pending_request_count,
      ],
      chart: {
        type: 'donut',
        height: 278,
        fontFamily: "Inter, 'Noto Sans Thai', ui-sans-serif, system-ui, sans-serif",
        sparkline: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      labels: ['รับรองแล้ว', 'ยังไม่รับรอง'],
      colors: ['var(--bs-success)', 'var(--bs-warning)'],
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '66%',
            labels: {
              show: true,
              name: {
                show: true,
                color: 'var(--bs-gray-700)',
                fontSize: '12px',
                fontWeight: 700,
                offsetY: 18,
              },
              value: {
                show: true,
                color: 'var(--bs-gray-900)',
                fontSize: '26px',
                fontWeight: 800,
                offsetY: -14,
                formatter: () => String(summary.total_request_count),
              },
              total: {
                show: true,
                showAlways: true,
                label: 'รวมทั้งหมด',
                color: 'var(--bs-gray-700)',
                fontSize: '12px',
                fontWeight: 700,
                formatter: () => String(summary.total_request_count),
              },
            },
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 230,
            },
          },
        },
      ],
      stroke: {
        show: true,
        width: 4,
        colors: ['var(--bs-white)'],
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${value} รายการ`,
        },
      },
    };
  }

  get workloadChartOptions(): WorkloadChartOptions {
    const summary = this.summary();
    const minimumTeachingWorkload = summary.minimum_teaching_workload;
    const axisMax = Math.max(
      200,
      Math.ceil(Math.max(
        minimumTeachingWorkload,
        summary.teaching_workload,
        summary.total_teaching_workload,
      ) / 10) * 10,
    );

    return {
      annotations: {
        xaxis: [
          {
            x: minimumTeachingWorkload,
            borderColor: 'var(--bs-gray-600)',
            strokeDashArray: 4,
            label: {
              borderColor: 'var(--bs-gray-600)',
              style: {
                background: 'var(--bs-gray-600)',
                color: 'var(--bs-white)',
                fontSize: '11px',
                fontWeight: 700,
              },
              text: `${this.formatPlainNumber(minimumTeachingWorkload)} ชม.`,
            },
          },
        ],
      },
      series: [
        {
          name: 'ชั่วโมง',
          data: [
            minimumTeachingWorkload,
            summary.teaching_workload,
            summary.makeup_teaching_workload,
            summary.total_teaching_workload,
            summary.excess_hours,
          ],
        },
      ],
      chart: {
        type: 'bar',
        height: 298,
        fontFamily: "Inter, 'Noto Sans Thai', ui-sans-serif, system-ui, sans-serif",
        toolbar: {
          show: false,
        },
      },
      colors: [
        'var(--bs-gray-400)',
        'var(--bs-gray-500)',
        'var(--bs-success)',
        'var(--bs-gray-600)',
        'var(--bs-danger)',
      ],
      dataLabels: {
        enabled: false,
      },
      fill: {
        opacity: 1,
      },
      grid: {
        borderColor: 'var(--bs-gray-200)',
        strokeDashArray: 0,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '20%',
          borderRadius: 0,
          distributed: true,
          dataLabels: {
            position: 'top',
          },
        },
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['transparent'],
      },
      tooltip: {
        y: {
          formatter: (value: number) => this.formatHours(value),
        },
      },
      xaxis: {
        categories: [
          'ภาระงานขั้นต่ำ',
          'ภาระงานตามตารางสอน',
          'ภาระงานที่สอนชดเชย',
          'ภาระงานรวม',
          'ภาระงานเกิน',
        ],
        min: 0,
        max: axisMax,
        tickAmount: 4,
        labels: {
          style: {
            colors: 'var(--bs-gray-600)',
            fontSize: '12px',
            fontWeight: 600,
          },
        },
        title: {
          text: 'ชั่วโมง',
          style: {
            color: 'var(--bs-gray-600)',
            fontSize: '12px',
            fontWeight: 700,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: 'var(--bs-gray-800)',
            fontSize: '12px',
            fontWeight: 700,
          },
        },
      },
    };
  }

  ngOnInit(): void {
    this.initForm();
    this.listenFilters();
    this.loadAcademicTerms();
  }

  getMetricClass(metric: CompensationSummaryMetric): string {
    return metric.tone == null
      ? 'compensation-summary-dashboard-metric-default'
      : `compensation-summary-dashboard-metric-${metric.tone}`;
  }

  formatHours(value: number | null | undefined): string {
    const numericValue = this.normalizeNumber(value);
    const formattedValue = this.formatPlainNumber(numericValue);

    return `${formattedValue} ชม.`;
  }

  formatPlainNumber(value: number | null | undefined): string {
    const numericValue = this.normalizeNumber(value);
    const formattedValue = Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2).replace(/\.?0+$/, '');

    return formattedValue;
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
    });
  }

  private listenFilters(): void {
    this.form.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.loadSummary());
  }

  private loadAcademicTerms(): void {
    this.studyPeriodService.getAcademicTermYears()
      .pipe(take(1))
      .subscribe({
        next: (terms) => {
          this.academicTerms = terms;
          this.academicTermOptions = this.buildAcademicTermOptions(terms);
          this.yearOptions = this.buildAcademicYearOptions(terms);
          this.ensureFilterDefaults();
          this.loadSummary();
        },
        error: (err) => {
          console.error(err);
          this.loadSummary();
        },
      });
  }

  private loadSummary(): void {
    this.isLoading.set(true);
    this.hasLoadError.set(false);

    this.teachCompensationService.getCompensationSummary(
      this.buildAcademicTermFilterParams(),
    ).pipe(
      finalize(() => this.isLoading.set(false)),
    ).subscribe({
      next: (summary) => {
        this.summary.set({
          total_request_count: this.normalizeNumber(summary.total_request_count),
          approved_request_count: this.normalizeNumber(summary.approved_request_count),
          pending_request_count: this.normalizeNumber(summary.pending_request_count),
          total_requested_hours: this.normalizeNumber(summary.total_requested_hours),
          approved_hours: this.normalizeNumber(summary.approved_hours),
          pending_hours: this.normalizeNumber(summary.pending_hours),
          minimum_teaching_workload: this.normalizeNumber(summary.minimum_teaching_workload),
          teaching_workload: this.normalizeNumber(summary.teaching_workload),
          makeup_teaching_workload: this.normalizeNumber(summary.makeup_teaching_workload),
          total_teaching_workload: this.normalizeNumber(summary.total_teaching_workload),
          excess_hours: this.normalizeNumber(summary.excess_hours),
          academic_term: summary.academic_term ?? null,
          erp_comparison: summary.erp_comparison,
        });
      },
      error: (err) => {
        console.error(err);
        this.hasLoadError.set(true);
        this.summary.set(this.createEmptySummary());
      },
    });
  }

  private buildAcademicTermFilterParams(): CompensationSummaryParams {
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '').trim();
    const buddhistYear = Number(this.form.get('year')?.value);
    const academicTermYear = Number.isFinite(buddhistYear)
      ? buddhistYear - 543
      : null;
    const matchedAcademicTerm = this.findAcademicTerm(academicTerm, String(buddhistYear));

    if (matchedAcademicTerm != null) {
      return { academic_term_id: matchedAcademicTerm.id };
    }

    return {
      academic_term_name: academicTerm,
      academic_term_year: academicTermYear,
    };
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.name).trim()).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));

    return options.length > 0 ? options : this.academicTermOptions;
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    const options = Array.from(new Set(
      terms.map((term) => String(term.year + 543)),
    )).sort((a, b) => Number(b) - Number(a));

    return options.length > 0 ? options : this.yearOptions;
  }

  private ensureFilterDefaults(): void {
    const academicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const year = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(academicTerm, year) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private findAcademicTerm(academicTerm: string, buddhistYear: string): AcademicTermYearModel | undefined {
    const normalizedTerm = String(academicTerm).trim();
    const normalizedYear = Number(buddhistYear);

    return this.academicTerms.find((term) => (
      String(term.name).trim() === normalizedTerm &&
      this.toBuddhistYear(term.year) === normalizedYear
    ));
  }

  private getDefaultAcademicTermName(): string {
    return String(this.currentUser()?.academic_term?.name ?? '1');
  }

  private getDefaultBuddhistYear(): string {
    const currentYear = new Date().getFullYear();
    const year = this.currentUser()?.academic_term?.year ?? currentYear;

    return String(this.toBuddhistYear(year));
  }

  private toBuddhistYear(year: number): number {
    return year > 2400 ? year : year + 543;
  }

  private normalizeNumber(value: number | string | null | undefined): number {
    const numericValue = Number(value ?? 0);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private formatPercent(value: number, total: number): string {
    if (total <= 0) {
      return '0';
    }

    return ((value / total) * 100).toFixed(2).replace(/\.?0+$/, '');
  }

  private formatDateTime(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private currentUser() {
    return this.authService.currentUser() ?? this.authService.currentUserValue ?? null;
  }

  private createEmptySummary(): CompensationSummaryResponse {
    return {
      total_request_count: 0,
      approved_request_count: 0,
      pending_request_count: 0,
      total_requested_hours: 0,
      approved_hours: 0,
      pending_hours: 0,
      minimum_teaching_workload: 0,
      teaching_workload: 0,
      makeup_teaching_workload: 0,
      total_teaching_workload: 0,
      excess_hours: 0,
      academic_term: null,
      erp_comparison: { has_result: false },
    };
  }
}
